/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * @author Anuja Gaikwad
 * ReviewQuestions.js
 * Styles for Report question components
 */

import { makeStyles } from '@material-ui/core/styles';
import mixins from '../../../assets/styles/mixins.scss';

const PADDING = 16;

export default makeStyles(() => ({
  /* DIALOG */
  dialogPaper: {
    borderRadius: 24,
    background: mixins.chalkWhiteFont,
    padding: 48,
    maxWidth: 800,
    maxHeight: 878,
    width: '100%'
  },

  dialogTitle: {
    padding: 0,
    margin: 0
  },

  title: {
    display: 'flex',
    alignItems: 'center',
    fontFamily: mixins.fontFamilyPlusSans,
    fontWeight: mixins.fontWeight500,
    fontSize: mixins.fontSize24,
    lineHeight: mixins.lineHeight32,
    color: mixins.color_text_primary,
    letterSpacing: '-0.48px'
  },

  question: {
    display: 'flex',
    alignItems: 'center',
    fontFamily: mixins.fontFamilyPlusSans,
    fontWeight: mixins.fontWeight600,
    fontSize: mixins.fontSize18,
    lineHeight: mixins.lineHeight22,
    color: mixins.color_text_primary
  },

  dialogContent: {
    padding: 0,
    marginTop: 8,
    overflow: 'hidden'
  },

  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16
  },
  contentRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12
  },

  checkboxGroup: {
    display: 'flex',
    flexDirection: 'column',

    /* 8px vertical gap */
    '& > *:not(:last-child)': {
      marginBottom: 8
    }
  },

  checkboxRow: {
    display: 'flex',
    alignItems: 'center'
  },

  customCheckbox: {
    padding: 0,
    border: 0,
    marginRight: 12,
    color: mixins.amethyst,
    '&.Mui-checked': {
      color: mixins.amethyst
    },
    '& svg': {
      fontSize: 18
    },
    '&.Mui-disabled': {
      '& $checkboxIcon': {
        fill: mixins.bulkEditCtaButtonDisabledBGColor,
        '& rect': {
          fill: mixins.bulkEditCtaButtonDisabledBGColor
        }
      }
    }
  },
  checkboxIcon: {},

  checkboxLabel: {
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize16,
    color: mixins.color_text_primary,
    fontWeight: mixins.fontWeight500,
    lineHeight: mixins.lineHeight20
  },

  textArea: {
    height: '160px',
    padding: 12,
    border: `1px solid ${mixins.amethyst}`,
    borderRadius: 4,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize16,
    color: mixins.greyScaleColor,
    fontWeight: mixins.fontWeight400,
    lineHeight: mixins.lineHeight24
  },
  studentReportLabel: {
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize16,
    color: mixins.color_text_primary,
    fontWeight: mixins.fontWeight600,
    lineHeight: mixins.lineHeight20
  },

  accordionRoot: {
    position: 'static',
    margin: 0,
    minHeight: 0,
    boxShadow: 'none',

    '&.Mui-expanded': {
      margin: 0
    },

    '&:before': {
      display: 'none'
    }
  },

  accordionSummaryRoot: {
    flexDirection: 'row-reverse', // moved from inline
    minHeight: 0,
    padding: 0,
    margin: 0,
    '&$accordionSummaryExpanded': {
      minHeight: '0 !important'
    },

    '& .MuiAccordionSummary-expandIcon': {
      marginLeft: 32,
      marginRight: 8
    }
  },

  accordionSummaryContent: {
    margin: '0 !important',
    '&$accordionSummaryExpanded': {
      margin: '0 !important'
    }
  },

  accordionDetailsRoot: {
    padding: 0,
    display: 'block'
  },

  accordionSummaryExpanded: {},

  badgeTemplateContainer: {
    display: 'flex',
    gap: 4,
    marginTop: 18,
    marginLeft: 32
  },

  commentsContainer: {
    height: '217px',
    marginLeft: '32px',
    padding: PADDING,
    borderRadius: 16,
    background: mixins.chalkWhiteFont,
    border: `1px solid ${mixins.flaggedChipColor}`,
    display: 'flex',
    flexDirection: 'column'
  },
  commentsTitle: {
    fontFamily: mixins.fontFamilyPlusSans,
    fontWeight: mixins.fontWeight600,
    fontSize: mixins.fontSize14,
    lineHeight: mixins.lineHeight16,
    color: mixins.color_text_primary,
    marginBottom: '12px',
    marginTop: '12px'
  },
  commentsItems: {
    fontFamily: mixins.fontFamilyPlusSans,
    fontWeight: mixins.fontWeight400,
    fontSize: mixins.fontSize14,
    lineHeight: mixins.lineHeight22,
    color: mixins.color_text_primary,
    marginBottom: 8
  },

  scrollArea: {
    flex: 1,
    overflow: 'auto'
  },

  /* FOOTER */
  footer: {
    padding: 0,
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 8,
    paddingTop: 16
  },
  dialogActions: {
    margin: 0,
    padding: 0
  },
  disregards: {
    height: 40,
    padding: 8,
    borderRadius: 1000,
    background: mixins.chalkWhiteFont,
    border: `2px solid ${mixins.navyColor}`,
    fontFamily: mixins.fontFamilyPlusSans,
    fontWeight: mixins.fontWeight500,
    fontSize: mixins.fontSize14,
    lineHeight: mixins.lineHeight22,
    color: mixins.navyColor,
    '&:hover': {
      background: mixins.secondaryButtonHoverColor
    }
  },
  reportInaccurate: {
    height: 40,
    borderRadius: 1000,
    background: mixins.amethyst,
    fontFamily: mixins.fontFamilyPlusSans,
    fontWeight: mixins.fontWeight500,
    fontSize: mixins.fontSize14,
    lineHeight: mixins.lineHeight22,
    color: mixins.chalkWhiteFont,
    '&:hover': {
      backgroundColor: mixins.amethystColorV2,
      color: mixins.chalkWhiteFont,
      boxShadow: 'none'
    }
  },
  /* CLOSE */
  closeButton: {
    position: 'absolute',
    top: 16,
    right: 16
  },
  primaryCol1: {
    padding: '0px 4px'
  }
}));
